import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import BreadcrumbComponent from "../../common/components/breadcrumb-component/breadcrumb-component";
import TextInputComponent from "../../common/components/text-input-component/text-input-component";
import { AlertComponent, PhoneNumberComponent } from "../../common/components";
import "./profile.scss";
import avatarIcon from "../../assets/icons/avatar.svg";
import { getCurrentUser } from "../../common/routines/user-operations";
import CountryCodeWithPhoneNumber from "../../common/components/phone-number-input-component/country-code-phone-numner-input";
import phoneValidation from "../../common/components/phone-number-input-component/phone-validation";
import {
  getUserDetails,
  updateProfileUsers,
  uploadUserProfileImage,
} from "../../api/settings";
import { getCurrentTenantId } from "../../utils/utils";
import { useSetLoading } from "../../common/hooks/appearance";
import uploadIcon from "../../assets/icons/camera-icon.svg";
import store from "../../store";
import AddressFormFields from "../../common/components/address-form-fields/address-form-fields";

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone_number: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  profile_url?: string;
};

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone_number: Yup.string()
    .test(
      "is-valid-phone",
      "Please enter a valid phone number",
      function (value) {
        return phoneValidation(value);
      }
    )
    .required("Phone number is required "),
  // address_line_1: Yup.string().required("Address Line 1 is required"),
  // city: Yup.string().required("City is required"),
  // state: Yup.string().required("State is required"),
  // zip: Yup.string().required("ZIP is required"),
  // country: Yup.string().required("Country is required"),
});

const Profile: React.FC = () => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const crumbMapping = [{ label: "Profile", link: "/profile" }];
  const [profile, setProfile] = useState<any>(null);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: string;
    type?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const tenantId = getCurrentTenantId();
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const response = await getUserDetails(userId);
      setProfile(response.data?.data?.data?.data);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message || "Failed to fetch user profile",
        type: "error",
      });
      console.error("Error fetching user profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUserProfile();
  }, [userId]);
  const userInfo = profile?.find((user: any) => userId === user?.id);

  const initialValues: ProfileFormValues = {
    firstName: userInfo?.first_name,
    lastName: userInfo?.last_name,
    email: userInfo?.email,
    phone_number: userInfo?.phone_number,
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    profile_url: userInfo?.profile_url || "",
  };

  const allowedImageTypes = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/webp",
    "image/svg+xml"
  ];

  const max_profile_image_size_in_MB = 110;

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!allowedImageTypes.includes(file.type)) {
        setAlert({
          isOpen: true,
          message: "Only JPG, JPEG, PNG, SVG or WEBP image formats are allowed.",
          type: "error",
        });
        setProfileImageFile(null);
        return;
      }
      if (file.size > max_profile_image_size_in_MB * 1024 * 1024) {
        setAlert({
          isOpen: true,
          message: `Image size should not exceed ${max_profile_image_size_in_MB}MB.`,
          type: "error",
        });
        setProfileImageFile(null);
        return;
      }
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfileImage(e.target?.result as string);
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const userCurrentDetails = profile?.find(
      (user: any) => userId === user?.id
    );

    if (!userCurrentDetails) {
      console.error("Primary user not found.");
      return;
    }

    try {
      setLoading(true);
      let profileUrl = userCurrentDetails.profile_url || ""; 
      if (profileImageFile) {
        const formData = new FormData();
        formData.append("file", profileImageFile);
        const profileResponse = await uploadUserProfileImage(formData);
        profileUrl = profileResponse?.data?.data || profileUrl;
      }
      const userPayload = {
        users: [
          {
            id: userCurrentDetails.id,
            isUpdate: true,
            isPrimaryUser: userCurrentDetails?.is_primary_user || false,
            roleId: userCurrentDetails?.role?.id,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phone_number,
            profileUrl: profileUrl,
          },
        ],
      };
      const response = await updateProfileUsers(Number(tenantId), userPayload);
      if (response?.data?.success) {
        setProfile((prev: any[]) => {
          return prev.map((user) => {
            if (user.id === userCurrentDetails.id) {
              const profile_url = response.data.data[0].profile_url;
              const loginDataStr = localStorage.getItem("loginData");
              if (loginDataStr) {
                const loginData = JSON.parse(loginDataStr);
                loginData.userDetails.profile_url = profile_url;
                localStorage.setItem("loginData", JSON.stringify(loginData));
                store.dispatch({
                  type: "SESSION:SET_IS_LOGGED_IN",
                  payload: {
                    value: true,
                    tenantDetails: loginData.tenantDetails,
                    userDetails: loginData.userDetails,
                  },
                });
              }
              return {
                ...user,
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                phone_number: values.phone_number,
                profile_url: profile_url,
              };
            }
            return user;
          });
        });
        setAlert({
          isOpen: true,
          message:
            response?.data?.message || "Personal details updated successfully",
          type: "success",
        });
      } else {
        console.error("Update failed");
      }
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      setAlert({
        isOpen: true,
        message: error?.response?.data?.message || "Failed to update profile",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="profile-container profile-container-mobile-view">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => {
          return (
            <Form className="profile-form">
              <div className="profile-image-wrapper">
                <input
                  type="file"
                  id="profileImage"
                  hidden
                  accept={allowedImageTypes.join(",")}
                  onChange={handleImageChange}
                />
                <div className="profile-image-container">
                  <img
                    src={profileImage || userInfo?.profile_url || avatarIcon}
                    className="profile-icon"
                    alt="profile"
                    width={100}
                  />
                  <div
                    className="upload-icon-wrapper"
                    onClick={() =>
                      document.getElementById("profileImage")?.click()
                    }
                  >
                    <img
                      src={uploadIcon}
                      alt="Upload"
                      className="upload-icon"
                    />
                  </div>
                </div>
                <div className="profile-card">
                  <p>Full Name</p>
                  <div className="profile-name">
                    <h2>{userInfo?.first_name + " " + userInfo?.last_name}</h2>
                  </div>
                </div>
              </div>

              <div className="form-grid">
                <TextInputComponent
                  fieldName="firstName"
                  label="First Name"
                  type="text"
                  placeholder="Enter first name"
                  value={values.firstName}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  optional
                  disabled
                />

                <TextInputComponent
                  fieldName="lastName"
                  label="Last Name"
                  type="text"
                  placeholder="Enter last name"
                  value={values.lastName}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  optional
                  disabled
                />

                <TextInputComponent
                  fieldName="email"
                  label="Email"
                  type="email"
                  placeholder="Enter email"
                  value={values.email}
                  onChange={handleChange}
                  handleBlur={handleBlur}
                  optional
                />
                <div>
                  <CountryCodeWithPhoneNumber
                    fieldName="phonenumber"
                    label="Phone Number"
                    placeholder="Enter phone number"
                    onChange={(value: string, country: any) => {
                      const countryCode = country?.dialCode;

                      if (!countryCode) {
                        console.warn("Missing dialCode in country object");
                        return;
                      }

                      const numberOnly = value.slice(countryCode.length);
                      const formatted = `+${countryCode} ${numberOnly}`;

                      setFieldValue("phone_number", formatted);
                    }}
                    onBlur={() => {
                      setFieldTouched("phone_number", true);
                    }}
                    value={values?.phone_number || ""}
                    defaultCountry="us"
                  />
                  {errors?.phone_number && touched?.phone_number && (
                    <div className="error-message">{errors.phone_number}</div>
                  )}
                </div>
                <div className="full-width">

                    <AddressFormFields
                      postCode="zipcode"
                      namePrefix="mailingAddress"
                    />
                    </div>
                  </div>

              <button type="submit" className="submit-button">
                Save Changes
              </button>
            </Form>
          );
        }}
      </Formik>
      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
    </div>
  );
};

export default Profile;
